/**
 * Comparison badge showing a delta value with directional coloring.
 * Respects higherIsBetter to determine whether the delta is positive or negative.
 * Format: up-arrow 0.6% vs 7d ago (or custom label)
 */

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

import { fmtDelta } from '@/lib/format';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export interface ComparisonBadgeProps {
  delta: number;
  unit: string;
  higherIsBetter: boolean;
  label?: string;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ComparisonBadge({
  delta,
  unit,
  higherIsBetter,
  label,
  className,
}: ComparisonBadgeProps) {
  const isZero = delta === 0;
  const isPositiveDelta = delta > 0;

  // "Good" means the delta direction aligns with higherIsBetter
  const isGood = isZero ? false : higherIsBetter ? isPositiveDelta : !isPositiveDelta;

  // Format delta value
  const formattedDelta = fmtDelta(delta, unit);

  // Colors
  const colorClass = isZero
    ? 'text-secondary bg-muted'
    : isGood
      ? 'text-success bg-success/10'
      : 'text-danger bg-danger/10';

  // Icon
  const Icon = isZero ? Minus : isPositiveDelta ? TrendingUp : TrendingDown;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        colorClass,
        className
      )}
    >
      <Icon className="w-3 h-3" />
      <span>{formattedDelta}</span>
      {label && <span className="text-secondary/70">{label}</span>}
    </span>
  );
}
